// import library
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
// import component
import Layout from "../../../layouts/Layout";
import LoaderIcon from "../../../components/Common/Icon/Loader";
import { env } from "../../../env";
import { AuthContext } from "../../../context";
// import child components
import ChatHeader from "./ChatHeader";
import ChatSidebar from "./ChatSidebar";
import ChatTopBar from "./ChatTopBar";
import MessageList from "./MessageList";
import ChatComposer from "./ChatComposer";
import DeleteConfirmModal from "./DeleteConfirmModal";
const SESSION_PAGE_SIZE = 10;

const FALLBACK_SALESPERSON_ID = "70";
const CHIP_CLASSES = [
    "from-sky-200/90 to-indigo-200/70 text-indigo-900",
    "from-pink-200/90 to-fuchsia-200/70 text-fuchsia-900",
    "from-amber-200/90 to-orange-200/70 text-amber-950",
    "from-emerald-200/90 to-teal-200/70 text-emerald-950",
];

const nowIso = () => new Date().toISOString();
const shortTitle = (text) =>
    text.length > 25 ? `${text.slice(0, 25).trim()}...` : text;

const formatTimestamp = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDayLabel = (isoString) => {
    if (!isoString) return "Today";
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (a, b) =>
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate();

    if (isSameDay(date, today)) return "Today";
    if (isSameDay(date, yesterday)) return "Yesterday";
    return date.toLocaleDateString();
};

const AiChat = () => {
    const { t } = useTranslation();
    const { aiChatBaseUrl, aiChatAuth } = useContext(AuthContext);

    const { id } = useParams();
    const customerId = id || "";

    const [query, setQuery] = useState("");
    const [activeFilter, setActiveFilter] = useState("all");
    const [viewDensity, setViewDensity] = useState("comfortable");
    const [isLoadingChats, setIsLoadingChats] = useState(true);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [sessionPage, setSessionPage] = useState(1);
    const [sessionHasNext, setSessionHasNext] = useState(false);
    const [sidebarWidth, setSidebarWidth] = useState(320);
    const [isResizing, setIsResizing] = useState(false);
    const [isLarge, setIsLarge] = useState(() =>
        typeof window !== "undefined" ? window.innerWidth >= 1024 : true
    );

    const [draft, setDraft] = useState("");
    const [sessionId, setSessionId] = useState("");
    const [isSending, setIsSending] = useState(false);

    const [renamingChatId, setRenamingChatId] = useState("");
    const [renameValue, setRenameValue] = useState("");
    const [copyNoticeId, setCopyNoticeId] = useState("");
    const [menuChatId, setMenuChatId] = useState("");
    const [confirmDeleteId, setConfirmDeleteId] = useState("");
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [chips, setChips] = useState([]);

    const [isCustomerLoading, setIsCustomerLoading] = useState(true);
    const [customerData, setCustomerData] = useState({});
    const [customerCode, setCustomerCode] = useState("");
    const [salespersonId, setSalespersonId] = useState("");

    const [chats, setChats] = useState([]);
    const [activeChatId, setActiveChatId] = useState("");

    const containerRef = useRef(null);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 100);
    };

    const activeChat = useMemo(
        () => chats.find((chat) => chat.id === activeChatId) || chats[0],
        [activeChatId, chats]
    );

    const pinnedChats = useMemo(
        () => chats.filter((chat) => chat.pinned),
        [chats]
    );
    const visiblePinned = useMemo(() => pinnedChats, [pinnedChats]);
    const recentChats = useMemo(
        () => chats.filter((chat) => !chat.pinned),
        [chats]
    );

    const groupedMessages = useMemo(() => {
        if (!activeChat?.messages?.length) return [];
        const sorted = [...activeChat.messages].sort((a, b) =>
            (a.createdAt || "").localeCompare(b.createdAt || "")
        );
        const result = [];
        let lastLabel = "";
        sorted.forEach((msg) => {
            const label = formatDayLabel(msg.createdAt);
            if (label !== lastLabel) {
                result.push({ type: "separator", label });
                lastLabel = label;
            }
            result.push({ type: "message", message: msg });
        });
        return result;
    }, [activeChat]);

    const fetchSessions = async (
        page = 1,
        append = false,
        code = "",
        searchTerm = "",
        timeFilter = ""
    ) => {
        setIsLoadingChats(true);
        try {
            const effectiveCode = code || customerCode || customerId;
            const payload = {
                salesperson_id: (salespersonId || FALLBACK_SALESPERSON_ID),
                customer_id: effectiveCode,
                page,
                page_size: SESSION_PAGE_SIZE,
                search_term: searchTerm,
            };
            if (timeFilter && timeFilter !== "all") {
                payload.time_filter = timeFilter;
            }
            const response = await fetch(`${aiChatBaseUrl}/conversations`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: aiChatAuth,
                },
                body: JSON.stringify(payload),
            });


            if (!response.ok) throw new Error("Session list request failed");

            const data = await response.json();
            const items = Array.isArray(data?.data) ? data.data : [];
            const mapped = items.map((item) => ({
                id: item.session_id,
                title:
                    (item.original_query || item.metadata?.original_query) &&
                    (item.original_query || item.metadata?.original_query).trim()
                        ? shortTitle(item.original_query || item.metadata?.original_query)
                        : item.display_title && item.display_title.trim()
                        ? item.display_title
                        : t('aura default session title'),
                updatedAt: formatTimestamp(item.last_chat_time),
                isLocal: false,
                pinned: Boolean(item.is_pinned),
                unread: 0,
                messages: [],
                totalMessages: item.total_messages,
                sessionCreatedAt: item.session_created_at,
                lastChatTime: item.last_chat_time,
                historyLoaded: false,
            }));

            setChats((prev) => (append ? [...prev, ...mapped] : mapped));
            setSessionPage(page);
            setSessionHasNext(Boolean(data?.pagination?.has_next));

            if (!activeChatId && mapped.length) setActiveChatId(mapped[0].id);

            // Auto-start a new conversation when the customer has no sessions yet
            if (!append && mapped.length === 0) {
                const newChat = {
                    id: `local-${Date.now()}`,
                    title: t('aura new chat'),
                    updatedAt: t('aura topbar just now'),
                    isLocal: true,
                    historyLoaded: true,
                    pinned: false,
                    unread: 0,
                    messages: [
                        {
                            id: `m-${Date.now()}`,
                            role: "assistant",
                            content: t('aura default greeting'),
                            createdAt: nowIso(),
                            reaction: "",
                            saved: false,
                        },
                    ],
                };
                setChats([newChat]);
                setActiveChatId(newChat.id);
                setSessionId("");
            }
        } catch (error) {
            setSessionHasNext(false);
        } finally {
            setIsLoadingChats(false);
        }
    };

    const getCustomerDataApi = async () => {
        const baseUrl = env.REACT_APP_API_URL;
        const token = localStorage.getItem("token");

        setIsCustomerLoading(true);
        try {
            const response = await axios.post(
                `${baseUrl}/api/Customer/GetCustomerDetail`,
                { customerid: customerId },
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const customer = response?.data?.length ? response.data[0] : {};
            setCustomerData(customer);
            const spId =
                customer?.salesperson_id ||
                customer?.SalespersonId ||
                customer?.salespersonId ||
                customer?.salesPersonId ||
                "";
            return { code: customer?.code || "", spId };
        } catch (error) {
            if (
                error.response &&
                (error.response.status === 401 || error.response.status === 404)
            ) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        } finally {
            setIsCustomerLoading(false);
        }
        return { code: "", spId: "" };
    };

    const loadHistory = async (session) => {
        if (!session) return;
        setIsLoadingHistory(true);
        try {
            const response = await fetch(`${aiChatBaseUrl}/chat-histories`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: aiChatAuth,
                },
                body: JSON.stringify({
                    session_id: session,
                    page: 1,
                    page_size: 50,
                }),
            });

            if (!response.ok) throw new Error("Chat history request failed");

            const data = await response.json();
            const rawMessages = Array.isArray(data?.data?.messages)
                ? data.data.messages
                : Array.isArray(data?.data)
                ? data.data
                : [];
            const firstUserOriginalQuery = rawMessages
                .map((item) => item?.json ?? item)
                .find(
                    (entry) =>
                        entry?.role === "user" &&
                        (entry?.original_query ||
                            entry?.metadata?.original_query) &&
                        (entry?.original_query ||
                            entry?.metadata?.original_query).trim()
                );
            const resolvedOriginalQuery =
                firstUserOriginalQuery?.original_query ||
                firstUserOriginalQuery?.metadata?.original_query ||
                "";

            const normalized = rawMessages
                .map((item) => item?.json ?? item)
                .filter(Boolean)
                .map((entry) => ({
                    id: entry.id ? `msg-${entry.id}` : `msg-${entry.created_at || Math.random()}`,
                    role: entry.role,
                    content:
                        entry.original_query ||
                        entry.metadata?.original_query ||
                        entry.content ||
                        "",
                    createdAt: entry.created_at || nowIso(),
                    reaction: "",
                    saved: false,
                }))
                .sort((a, b) =>
                    (a.createdAt || "").localeCompare(b.createdAt || "")
                );

            setChats((prev) =>
                prev.map((chat) =>
                    chat.id === session
                        ? {
                              ...chat,
                              title: resolvedOriginalQuery
                                  ? shortTitle(resolvedOriginalQuery)
                                  : chat.title,
                              messages: normalized,
                              historyLoaded: true,
                          }
                        : chat
                )
            );
            scrollToBottom();
        } catch (error) {
            // ignore history error
        } finally {
            setIsLoadingHistory(false);
        }
    };

    useEffect(() => {
        if (!aiChatBaseUrl) return;
        setChats([]);
        setActiveChatId("");
        setSessionId("");
        setSessionPage(1);
        setSessionHasNext(false);
        setCustomerCode("");
        getCustomerDataApi().then(({ code, spId }) => {
            const nextCode = code || customerId;
            setCustomerCode(nextCode);
            if (spId) setSalespersonId(spId);
            fetchSessions(1, false, nextCode, query, activeFilter);
        });
    }, [customerId, aiChatBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!customerCode) return;
        const timer = setTimeout(() => {
            setChats([]);
            setActiveChatId("");
            setSessionId("");
            setSessionPage(1);
            setSessionHasNext(false);
            fetchSessions(1, false, customerCode, query, activeFilter);
        }, 400);
        return () => clearTimeout(timer);
    }, [query, activeFilter, customerCode]);

    useEffect(() => {
        const handleResize = () => setIsLarge(window.innerWidth >= 1024);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const fetchStarterQuestions = async () => {
            try {
                const response = await fetch(`${aiChatBaseUrl}/starter-questions`, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });
                if (!response.ok) throw new Error("Starter questions request failed");

                const payload = await response.json();
                const list = Array.isArray(payload?.data) ? payload.data : [];
                const mapped = list
                    .filter((item) => item?.is_active !== false && item?.description)
                    .map((item, index) => ({
                        label: item.title?.trim() || `Suggestion ${index + 1}`,
                        cls: CHIP_CLASSES[index % CHIP_CLASSES.length],
                        question: item.description.trim(),
                    }));

                if (mapped.length > 0) {
                    setChips(mapped);
                }
            } catch (error) {
                // leave chips empty on error
            }
        };

        if (aiChatBaseUrl) fetchStarterQuestions();
    }, [aiChatBaseUrl]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!activeChatId) return;
        const current = chats.find((chat) => chat.id === activeChatId);
        if (current && !current.isLocal && !current.historyLoaded) {
            loadHistory(activeChatId);
            setSessionId(activeChatId);
        }
    }, [activeChatId, chats]);

    useEffect(() => {
        const handleKey = (event) => {
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
                event.preventDefault();
                handleNewChat();
            }
            if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
                event.preventDefault();
                handleSend();
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    });

    useEffect(() => {
        const handleMove = (event) => {
            if (!isResizing) return;
            const container = containerRef.current;
            if (!container) return;
            const rect = container.getBoundingClientRect();
            const nextWidth = Math.min(420, Math.max(260, event.clientX - rect.left));
            setSidebarWidth(nextWidth);
        };
        const handleUp = () => setIsResizing(false);
        window.addEventListener("mousemove", handleMove);
        window.addEventListener("mouseup", handleUp);
        return () => {
            window.removeEventListener("mousemove", handleMove);
            window.removeEventListener("mouseup", handleUp);
        };
    }, [isResizing]);

    const handleNewChat = () => {
        const newChat = {
            id: `local-${Date.now()}`,
            title: t('aura new chat'),
            updatedAt: t('aura topbar just now'),
            isLocal: true,
            historyLoaded: true,
            pinned: false,
            unread: 0,
            messages: [
                {
                    id: `m-${Date.now()}`,
                    role: "assistant",
                    content: t('aura default greeting'),
                    createdAt: nowIso(),
                    reaction: "",
                    saved: false,
                },
            ],
        };
        setChats((prev) => [newChat, ...prev]);
        setActiveChatId(newChat.id);
        setSessionId("");
    };

    const handleSend = async (questionText = null) => {
        const trimmed = (typeof questionText === "string" && questionText) ? questionText.trim() : draft.trim();
        if (!trimmed || isSending) return;
        setIsSending(true);
        const safeSessionId =
            sessionId && !String(sessionId).startsWith("local-")
                ? sessionId
                : undefined;

        setChats((prev) =>
            prev.map((chat) => {
                if (chat.id !== activeChatId) return chat;
                const updatedTitle =
                    chat.title === t('aura new chat') ? shortTitle(trimmed) : chat.title;
                return {
                    ...chat,
                    title: updatedTitle,
                    updatedAt: t('aura topbar just now'),
                    messages: [
                        ...chat.messages,
                        {
                            id: `m-${Date.now()}`,
                            role: "user",
                            content: trimmed,
                            createdAt: nowIso(),
                        },
                    ],
                };
            })
        );
        if (!(typeof questionText === "string" && questionText)) setDraft("");
        scrollToBottom();

        try {
            const response = await fetch(`${aiChatBaseUrl}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: aiChatAuth,
                },
                body: JSON.stringify({
                    salesperson_id: (salespersonId || FALLBACK_SALESPERSON_ID),
                    customer_id: customerCode || customerId,
                    query: trimmed,
                    session_id: safeSessionId,
                }),
            });

            if (!response.ok) throw new Error("Chat API request failed");

            const data = await response.json();
            const replyText = data?.content || t('aura no response');
            const newSessionId = data?.session_id || safeSessionId || "";

            if (newSessionId) setSessionId(newSessionId);

            setChats((prev) =>
                prev.map((chat) => {
                    if (chat.id !== activeChatId) return chat;
                    return {
                        ...chat,
                        id: chat.isLocal && newSessionId ? newSessionId : chat.id,
                        isLocal: false,
                        historyLoaded: true,
                        updatedAt: t('aura topbar just now'),
                        messages: [
                            ...chat.messages,
                            {
                                id: `m-${Date.now()}`,
                                role: "assistant",
                                content: replyText,
                                createdAt: nowIso(),
                                reaction: "",
                                saved: false,
                            },
                        ],
                    };
                })
            );
            scrollToBottom();
            if (newSessionId && activeChatId.startsWith("local-")) {
                setActiveChatId(newSessionId);
            }
        } catch (error) {
            setChats((prev) =>
                prev.map((chat) => {
                    if (chat.id !== activeChatId) return chat;
                    return {
                        ...chat,
                        updatedAt: t('aura topbar just now'),
                        messages: [
                            ...chat.messages,
                            {
                                id: `m-${Date.now()}`,
                                role: "assistant",
                                content: t('aura request failed'),
                                createdAt: nowIso(),
                                reaction: "",
                                saved: false,
                            },
                        ],
                    };
                })
            );
        } finally {
            setIsSending(false);
        }
    };

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (error) {
            // ignore clipboard failure
        }
    };

    const updateMessage = (messageId, updates) => {
        setChats((prev) =>
            prev.map((chat) => {
                if (chat.id !== activeChatId) return chat;
                return {
                    ...chat,
                    messages: chat.messages.map((msg) =>
                        msg.id === messageId ? { ...msg, ...updates } : msg
                    ),
                };
            })
        );
    };
    const togglePin = async (chatId) => {
        const chat = chats.find((c) => c.id === chatId);
        if (!chat) return;
        const newPinned = !chat.pinned;
        setChats((prev) =>
            prev.map((c) =>
                c.id === chatId ? { ...c, pinned: newPinned } : c
            )
        );
        try {
            await fetch(`${aiChatBaseUrl}/pin-conversation`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: aiChatAuth,
                },
                body: JSON.stringify({
                    session_id: chatId,
                    is_pinned: newPinned,
                    salesperson_id: (salespersonId || FALLBACK_SALESPERSON_ID),
                }),
            });
        } catch (error) {
            // revert on failure
            setChats((prev) =>
                prev.map((c) =>
                    c.id === chatId ? { ...c, pinned: !newPinned } : c
                )
            );
        }
    };
    const deleteChat = async (chatId) => {
        try {
            await fetch(`${aiChatBaseUrl}/delete-conversation`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: aiChatAuth,
                },
                body: JSON.stringify({
                    session_id: chatId,
                    salesperson_id: (salespersonId || FALLBACK_SALESPERSON_ID),
                }),
            });
        } catch (error) {
            // proceed with local removal even if API fails
        }
        setChats((prev) => prev.filter((chat) => chat.id !== chatId));
        if (activeChatId === chatId) {
            setActiveChatId("");
            setSessionId("");
        }
    };

    const handleRenameStart = () => {
        if (!activeChat) return;
        setRenamingChatId(activeChat.id);
        setRenameValue(activeChat.title);
    };

    const handleRenameCommit = async () => {
        if (!renamingChatId) return;
        const newTitle = renameValue.trim();
        if (!newTitle) {
            setRenamingChatId("");
            setRenameValue("");
            return;
        }
        const oldTitle = chats.find((c) => c.id === renamingChatId)?.title;
        setChats((prev) =>
            prev.map((chat) =>
                chat.id === renamingChatId
                    ? { ...chat, title: newTitle }
                    : chat
            )
        );
        setRenamingChatId("");
        setRenameValue("");
        try {
            await fetch(`${aiChatBaseUrl}/update-conversation-title`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: aiChatAuth,
                },
                body: JSON.stringify({
                    session_id: renamingChatId,
                    new_title: newTitle,
                    salesperson_id: (salespersonId || FALLBACK_SALESPERSON_ID),
                }),
            });
        } catch (error) {
            // revert on failure
            if (oldTitle) {
                setChats((prev) =>
                    prev.map((chat) =>
                        chat.id === renamingChatId
                            ? { ...chat, title: oldTitle }
                            : chat
                    )
                );
            }
        }
    };

    const handleSelectChat = (chat) => {
        setActiveChatId(chat.id);
        setSessionId(chat.isLocal ? "" : chat.id);
        if (!chat.messages || chat.messages.length === 0) {
            setIsLoadingHistory(true);
        }
        if (!isLarge) setMobileSidebarOpen(false);
    };

    const customerDisplay =
        customerData?.name || customerData?.code || customerCode || customerId;
    const salespersonDisplay =
        customerData?.salesperson_id ||
        customerData?.SalespersonId ||
        customerData?.salespersonId ||
        customerData?.salesPersonId ||
        (salespersonId || FALLBACK_SALESPERSON_ID);
    const isInitialDataLoading = isCustomerLoading || (isLoadingChats && chats.length === 0);

    return (
        <Layout>
            {isInitialDataLoading ? (
                <LoaderIcon />
            ) : (
                <div
                    ref={containerRef}
                    className="flex flex-col h-[100dvh] overflow-hidden p-4
                    bg-[radial-gradient(circle_at_50%_12%,rgba(255,255,255,0.9),transparent_40%),radial-gradient(circle_at_20%_0%,rgba(125,211,252,0.45),transparent_45%),radial-gradient(circle_at_80%_0%,rgba(244,114,182,0.35),transparent_45%),radial-gradient(circle_at_50%_100%,rgba(167,139,250,0.28),transparent_45%)]
                    bg-[#F2F4FF]"
                >
                    <ChatHeader
                        t={t}
                        viewDensity={viewDensity}
                        setViewDensity={setViewDensity}
                        onToggleMobileSidebar={() => setMobileSidebarOpen((p) => !p)}
                    />

                    {/* Mobile sidebar overlay */}
                    {!isLarge && (
                        <>
                            {mobileSidebarOpen && (
                                <div
                                    className="fixed inset-0 bg-black/30 z-[55]"
                                    onClick={() => setMobileSidebarOpen(false)}
                                />
                            )}
                            <ChatSidebar
                                query={query}
                                setQuery={setQuery}
                                activeFilter={activeFilter}
                                setActiveFilter={setActiveFilter}
                                isLoadingChats={isLoadingChats}
                                visiblePinned={visiblePinned}
                                recentChats={recentChats}
                                activeChat={activeChat}
                                menuChatId={menuChatId}
                                sessionHasNext={sessionHasNext}
                                mobileSidebarOpen={mobileSidebarOpen}
                                isMobile={true}
                                onCloseMobile={() => setMobileSidebarOpen(false)}
                                onNewChat={handleNewChat}
                                onSelectChat={handleSelectChat}
                                onToggleMenu={(id) => setMenuChatId((prev) => prev === id ? "" : id)}
                                onTogglePin={(id) => { togglePin(id); setMenuChatId(""); }}
                                onRequestDelete={(id) => { setConfirmDeleteId(id); setMenuChatId(""); }}
                                onLoadMore={() => fetchSessions(sessionPage + 1, true, customerCode || customerId, query, activeFilter)}
                                setIsResizing={setIsResizing}
                            />
                        </>
                    )}

                    {/* Main frame with subtle glow */}
                    <div className="relative flex-1 min-h-0">
                        <div className="absolute -inset-1 rounded-[30px] bg-[radial-gradient(circle_at_20%_20%,rgba(125,211,252,0.35),transparent_40%),radial-gradient(circle_at_80%_20%,rgba(244,114,182,0.30),transparent_40%),radial-gradient(circle_at_50%_100%,rgba(167,139,250,0.25),transparent_45%)] blur-2xl" />
                        <div className="relative rounded-[28px] border border-white/70 bg-white/55 backdrop-blur-xl
                        shadow-[0_26px_90px_rgba(79,70,229,0.2)] overflow-hidden flex-1 min-h-0 h-full">
                            <div
                                className="grid h-full min-h-0"
                                style={{ gridTemplateColumns: isLarge ? `${sidebarWidth}px 1fr` : "1fr" }}
                            >
                                {isLarge && (
                                <ChatSidebar
                                    query={query}
                                    setQuery={setQuery}
                                    activeFilter={activeFilter}
                                    setActiveFilter={setActiveFilter}
                                    isLoadingChats={isLoadingChats}
                                    visiblePinned={visiblePinned}
                                    recentChats={recentChats}
                                    activeChat={activeChat}
                                    menuChatId={menuChatId}
                                    sessionHasNext={sessionHasNext}
                                    mobileSidebarOpen={true}
                                    isMobile={false}
                                    onNewChat={handleNewChat}
                                    onSelectChat={handleSelectChat}
                                    onToggleMenu={(id) => setMenuChatId((prev) => prev === id ? "" : id)}
                                    onTogglePin={(id) => { togglePin(id); setMenuChatId(""); }}
                                    onRequestDelete={(id) => { setConfirmDeleteId(id); setMenuChatId(""); }}
                                    onLoadMore={() => fetchSessions(sessionPage + 1, true, customerCode || customerId, query, activeFilter)}
                                    setIsResizing={setIsResizing}
                                />
                                )}

                                <section className="flex flex-col h-full min-h-0">
                                    <ChatTopBar
                                        activeChat={activeChat}
                                        renamingChatId={renamingChatId}
                                        renameValue={renameValue}
                                        setRenameValue={setRenameValue}
                                        onRenameStart={handleRenameStart}
                                        onRenameCommit={handleRenameCommit}
                                        customerDisplay={customerDisplay}
                                        salespersonDisplay={salespersonDisplay}
                                        chips={chips}
                                        onChipClick={(question) => handleSend(question)}
                                    />

                                    <MessageList
                                        viewDensity={viewDensity}
                                        isLoadingHistory={isLoadingHistory}
                                        groupedMessages={groupedMessages}
                                        isSending={isSending}
                                        copyNoticeId={copyNoticeId}
                                        messagesEndRef={messagesEndRef}
                                        onCopy={handleCopy}
                                        setCopyNoticeId={setCopyNoticeId}
                                        onUpdateMessage={updateMessage}
                                        onStartEdit={(msg) => setDraft(msg.content || "")}
                                        formatTimestamp={formatTimestamp}
                                    />

                                    <ChatComposer
                                        draft={draft}
                                        setDraft={setDraft}
                                        isSending={isSending}
                                        onSend={handleSend}
                                    />
                                </section>

                            </div>
                        </div>
                    </div>

                    <DeleteConfirmModal
                        confirmDeleteId={confirmDeleteId}
                        onCancel={() => setConfirmDeleteId("")}
                        onConfirm={() => { deleteChat(confirmDeleteId); setConfirmDeleteId(""); }}
                    />
                </div>
            )}
        </Layout>
    );
};

export default AiChat;
